import { useCallback } from 'react'
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import { useDocContentGeneration } from './useDocContentGeneration'
import { COMMENTS_ON_INSTRUCTIONS, DEFAULT_PDF_DOC_TXT } from './constants'
import {
  TCommonsRebateDocData,
  TCommunityRebateDocData,
  TVendorRebateDocData
} from './types'

export const useFinancialsRebatesDocGeneration = () => {
  const {
    generateDocumentInfo,
    generateFromToSection,
    generateCommentsAndTotalSection,
    launchDocGeneration,
    getPdfFileName,
    generateRebateInvoiceTable
  } = useDocContentGeneration()

  const _generateCommunityRemitAdviceDocHeader = useCallback(
    (communityData: TCommunityRebateDocData['communityData']): Content[] => {
      return [
        ...generateDocumentInfo({
          docTitle: DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
          docHeaderName: DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          docDate: communityData.remit_advice_date,
          docId: communityData.document_number,
          docDueOn: communityData.due_date
        }),
        ...generateFromToSection(
          {
            name: communityData.hospital_name,
            address: communityData.hospital_address,
            id: communityData.hospital_id
          },
          {
            name: communityData.supplier_name,
            address: communityData.supplier_address,
            phoneNumber: communityData.supplier_phone_number
          }
        )
      ]
    },
    [generateDocumentInfo, generateFromToSection]
  )

  const _generateCommonsRemitAdviceDocHeader = useCallback(
    (commonsData: TCommonsRebateDocData['commonsData']): Content[] => [
      ...generateDocumentInfo({
        docTitle: DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
        docHeaderName: DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
        docDate: commonsData.remit_advice_date,
        docId: commonsData.document_number,
        docDueOn: commonsData.due_date
      }),
      ...generateFromToSection(
        {
          name: commonsData.cognus_legal_name,
          address: commonsData.cognus_address,
          id: commonsData.hospital_id
        },
        {
          name: commonsData.supplier_name,
          address: commonsData.supplier_address,
          phoneNumber: commonsData.supplier_phone_number
        }
      )
    ],
    [generateDocumentInfo, generateFromToSection]
  )

  const _generateVendorInvoiceDocHeader = useCallback(
    (vendorData: TVendorRebateDocData['vendorData']): Content[] => [
      ...generateDocumentInfo({
        docTitle: DEFAULT_PDF_DOC_TXT.INVOICE,
        docHeaderName: DEFAULT_PDF_DOC_TXT.INVOICE,
        docDate: vendorData.invoice_date,
        docId: vendorData.document_number,
        docDueOn: vendorData.due_date
      }),
      ...generateFromToSection(
        {
          name: vendorData.hospital_name,
          address: vendorData.hospital_address,
          id: vendorData.hospital_id
        },
        {
          name: vendorData.supplier_name,
          address: vendorData.supplier_address,
          phoneNumber: vendorData.supplier_phone_number
        }
      )
    ],
    [generateDocumentInfo, generateFromToSection]
  )

  const generateCommunityRebateRemitAdviceDoc = useCallback(
    ({ communityData, callback }: TCommunityRebateDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateCommunityRemitAdviceDocHeader(communityData),
        ...generateRebateInvoiceTable(communityData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT_NET],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_REBATE,
            value: communityData.rebate_amount_pre_cf
          },
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.PROCESSING_FEE,
              value: communityData.processing_fee
            },
            {
              label: DEFAULT_PDF_DOC_TXT.NET_TO_HOSPITAL,
              value: communityData.rebate_amount
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          communityData.document_number
        ),
        callback
      )
    },
    [
      _generateCommunityRemitAdviceDocHeader,
      launchDocGeneration,
      generateRebateInvoiceTable,
      getPdfFileName,
      generateCommentsAndTotalSection
    ]
  )

  const generateCommonsRebateRemitAdviceDoc = useCallback(
    ({ commonsData, callback }: TCommonsRebateDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateCommonsRemitAdviceDocHeader(commonsData),
        ...generateRebateInvoiceTable(commonsData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_REBATE,
            value: commonsData.rebate_amount_pre_cf
          },
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.OM_COMMUNITY_FEE,
              value: commonsData.processing_fee
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          commonsData.document_number
        ),
        callback
      )
    },
    [
      _generateCommonsRemitAdviceDocHeader,
      launchDocGeneration,
      generateRebateInvoiceTable,
      getPdfFileName,
      generateCommentsAndTotalSection
    ]
  )

  const generateVendorRebateInvoiceDoc = useCallback(
    ({ vendorData, callback }: TVendorRebateDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ..._generateVendorInvoiceDocHeader(vendorData),
        ...generateRebateInvoiceTable(vendorData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
              value: vendorData.rebate_amount_pre_cf
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          vendorData.document_number
        ),
        callback
      )
    },
    [
      _generateVendorInvoiceDocHeader,
      launchDocGeneration,
      generateRebateInvoiceTable,
      getPdfFileName,
      generateCommentsAndTotalSection
    ]
  )

  return {
    generateCommunityRebateRemitAdviceDoc,
    generateCommonsRebateRemitAdviceDoc,
    generateVendorRebateInvoiceDoc
  }
}
